import { Currency, CurrencyAmount, ETHER, JSBI, Token, TokenAmount } from '@uniswap/sdk'
import { useMemo, useState, useEffect, useCallback, useRef } from 'react'
import ERC20_INTERFACE from '../../constants/abis/erc20'
import { useAllTokens } from '../../hooks/Tokens'
import { useActiveWeb3React } from '../../hooks'
import { useMulticallContract } from '../../hooks/useContract'
import { isAddress } from '../../utils'
import { useSingleContractMultipleData, useMultipleContractSingleData } from '../multicall/hooks'
import { ethers } from 'ethers'
import abi from '../../assets/abi.json'
import { ROUTER_ADDRESS } from '../../constants'
import { useTransactionAdder } from '../transactions/hooks'
import { toast } from 'react-toastify'

/**
 * Returns a map of the given addresses to their eventually consistent ETH balances.
 */
export function useETHBalances(
  uncheckedAddresses?: (string | undefined)[]
): { [address: string]: CurrencyAmount | undefined } {
  const multicallContract = useMulticallContract()

  const addresses: string[] = useMemo(
    () =>
      uncheckedAddresses
        ? uncheckedAddresses
            .map(isAddress)
            .filter((a): a is string => a !== false)
            .sort()
        : [],
    [uncheckedAddresses]
  )

  const results = useSingleContractMultipleData(
    multicallContract,
    'getEthBalance',
    addresses.map(address => [address])
  )

  return useMemo(
    () =>
      addresses.reduce<{ [address: string]: CurrencyAmount }>((memo, address, i) => {
        const value = results?.[i]?.result?.[0]
        if (value) memo[address] = CurrencyAmount.ether(JSBI.BigInt(value.toString()))
        return memo
      }, {}),
    [addresses, results]
  )
}

/**
 * Returns a map of token addresses to their eventually consistent token balances for a single account.
 */
export function useTokenBalancesWithLoadingIndicator(
  address?: string,
  tokens?: (Token | undefined)[]
): [{ [tokenAddress: string]: TokenAmount | undefined }, boolean] {
  const validatedTokens: Token[] = useMemo(
    () => tokens?.filter((t?: Token): t is Token => isAddress(t?.address) !== false) ?? [],
    [tokens]
  )

  const validatedTokenAddresses = useMemo(() => validatedTokens.map(vt => vt.address), [validatedTokens])

  const balances = useMultipleContractSingleData(validatedTokenAddresses, ERC20_INTERFACE, 'balanceOf', [address])

  const anyLoading: boolean = useMemo(() => balances.some(callState => callState.loading), [balances])

  return [
    useMemo(
      () =>
        address && validatedTokens.length > 0
          ? validatedTokens.reduce<{ [tokenAddress: string]: TokenAmount | undefined }>((memo, token, i) => {
              const value = balances?.[i]?.result?.[0]
              const amount = value ? JSBI.BigInt(value.toString()) : undefined
              if (amount) {
                memo[token.address] = new TokenAmount(token, amount)
              }
              return memo
            }, {})
          : {},
      [address, validatedTokens, balances]
    ),
    anyLoading
  ]
}

export function useTokenBalances(
  address?: string,
  tokens?: (Token | undefined)[]
): { [tokenAddress: string]: TokenAmount | undefined } {
  return useTokenBalancesWithLoadingIndicator(address, tokens)[0]
}

// get the balance for a single token/account combo
export function useTokenBalance(account?: string, token?: Token): TokenAmount | undefined {
  const tokenBalances = useTokenBalances(account, [token])
  if (!token) return undefined
  return tokenBalances[token.address]
}

export function useCurrencyBalances(
  account?: string,
  currencies?: (Currency | undefined)[]
): (CurrencyAmount | undefined)[] {
  const tokens = useMemo(() => currencies?.filter((currency): currency is Token => currency instanceof Token) ?? [], [
    currencies
  ])

  const tokenBalances = useTokenBalances(account, tokens)
  const containsETH: boolean = useMemo(() => currencies?.some(currency => currency === ETHER) ?? false, [currencies])
  const ethBalance = useETHBalances(containsETH ? [account] : [])

  // console.log(account, tokens, tokenBalances)

  return useMemo(
    () =>
      currencies?.map(currency => {
        if (!account || !currency) return undefined
        if (currency instanceof Token) return tokenBalances[currency.address]
        if (currency === ETHER) return ethBalance[account]
        return undefined
      }) ?? [],
    [account, currencies, ethBalance, tokenBalances]
  )
}

export function useCurrencyBalance(account?: string, currency?: Currency): CurrencyAmount | undefined {
  return useCurrencyBalances(account, [currency])[0]
}

// mimics useAllBalances
export function useAllTokenBalances(): { [tokenAddress: string]: TokenAmount | undefined } {
  const { account } = useActiveWeb3React()
  const allTokens = useAllTokens()
  const allTokensArray = useMemo(() => Object.values(allTokens ?? {}), [allTokens])
  const balances = useTokenBalances(account ?? undefined, allTokensArray)
  return balances ?? {}
}

/**
 * 返回奖池地址的 ETH 余额
 */
export function useRewardPoolBalance(poolAddress?: string): CurrencyAmount | undefined {
  const addresses = useMemo(() => (poolAddress ? [poolAddress] : []), [poolAddress])
  const ethBalances = useETHBalances(addresses)

  return useMemo(() => (poolAddress ? ethBalances[poolAddress] : undefined), [poolAddress, ethBalances])
}

/**
 * 返回默认奖池地址的 ETH 余额
 */
export function useDefaultRewardPoolBalance(): CurrencyAmount | undefined {
  const DEFAULT_LIQUIDITY_REWARD_POOL = '0xF9E1159e1453bCFe6fB78bA3828B0A1D97C6a5bb'
  return useRewardPoolBalance(DEFAULT_LIQUIDITY_REWARD_POOL)
}

/**
 * 获取用户可领取的流动性奖励
 * @param userAddress 用户地址
 * @returns 可领取的奖励金额
 */
export function useUnclaimedReward(
  userAddress?: string | null
): {
  unclaimedAmount: JSBI | undefined
  isLoading: boolean
  formattedAmount: string
  refresh: () => Promise<void>
} {
  const { library } = useActiveWeb3React()
  const [unclaimedAmount, setUnclaimedAmount] = useState<JSBI | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [formattedAmount, setFormattedAmount] = useState('0')

  const fetchUnclaimedReward = useCallback(async (isInitialLoad = false) => {
    if (!library || !userAddress) return

    if (isInitialLoad) setIsLoading(true)
    try {
      // 获取Router合约ABI
      const routerAbi = abi

      const routerContract = new ethers.Contract(ROUTER_ADDRESS, routerAbi, library)
      const unclaimed = await routerContract.userUnclaimed(userAddress)

      // 转换为CurrencyAmount
      const amount = JSBI.BigInt(unclaimed.toString())
      setUnclaimedAmount(amount)

      // 格式化显示金额
      const etherValue = parseFloat(ethers.utils.formatEther(unclaimed))
      console.log(etherValue)
      if (etherValue === 0) {
        setFormattedAmount('0 MUD')
      } else if (etherValue < 0.0001) {
        setFormattedAmount('< 0.0001 MUD')
      } else {
        // 修改为截取四位小数而不是四舍五入
        const truncated = Math.floor(etherValue * 10000) / 10000
        setFormattedAmount(`${truncated.toFixed(4)} MUD`)
      }
    } catch (error) {
      console.error('获取未领取奖励失败:', error)
    } finally {
      if (isInitialLoad) setIsLoading(false)
    }
  }, [library, userAddress])

  useEffect(() => {
    fetchUnclaimedReward(true)
  }, [fetchUnclaimedReward])

  return {
    unclaimedAmount,
    isLoading,
    formattedAmount,
    refresh: () => fetchUnclaimedReward(false)
  }
}

/**
 * 获取用户的流动性奖励信息
 * @param userAddress 用户地址
 * @returns 用户的流动性奖励信息
 */
export function useLiquidityRewardInfo(
  userAddress?: string | null
): {
  liquidityInfo: {
    liquidity: JSBI | undefined
    totalClaimed: JSBI | undefined
    unclaimed: JSBI | undefined
    lastUpdateTime: JSBI | undefined
  }
  isLoading: boolean
  formattedUnclaimed: string
  formattedTotalClaimed: string
  formattedLiquidity: string
  refresh: () => Promise<void>
} {
  const { library } = useActiveWeb3React()
  const [liquidityInfo, setLiquidityInfo] = useState<{
    liquidity: JSBI | undefined
    totalClaimed: JSBI | undefined
    unclaimed: JSBI | undefined
    lastUpdateTime: JSBI | undefined
  }>({
    liquidity: undefined,
    totalClaimed: undefined,
    unclaimed: undefined,
    lastUpdateTime: undefined
  })
  const [isLoading, setIsLoading] = useState(false)
  const [formattedUnclaimed, setFormattedUnclaimed] = useState('0')
  const [formattedTotalClaimed, setFormattedTotalClaimed] = useState('0')
  const [formattedLiquidity, setFormattedLiquidity] = useState('0')

  // 格式化金额的辅助函数
  const formatAmount = useCallback((amount: JSBI | undefined): string => {
    if (!amount || JSBI.EQ(amount, JSBI.BigInt(0))) return '0'

    const etherValue = parseFloat(ethers.utils.formatEther(amount.toString()))
    if (etherValue === 0) {
      return '0.00'
    } else if (etherValue < 0.0001) {
      return '< 0.0001'
    } else {
      // 修改为截取四位小数而不是四舍五入
      const truncated = Math.floor(etherValue * 10000) / 10000
      return truncated.toFixed(4)
    }
  }, [])

  const fetchLiquidityRewardInfo = useCallback(async (isInitialLoad = false) => {
    if (!library || !userAddress) return

    if (isInitialLoad) setIsLoading(true)
    try {
      // 获取Router合约ABI
      const routerAbi = abi

      const routerContract = new ethers.Contract(ROUTER_ADDRESS, routerAbi, library)
      const reward = await routerContract.liquidityRewards(userAddress)

      const liquidityValue = JSBI.BigInt(reward.liquidity.toString())
      const totalClaimedValue = JSBI.BigInt(reward.totalClaimed.toString())
      const unclaimedValue = JSBI.BigInt(reward.unclaimed.toString())
      const lastUpdateTimeValue = JSBI.BigInt(reward.lastUpdateTime.toString())

      setLiquidityInfo({
        liquidity: liquidityValue,
        totalClaimed: totalClaimedValue,
        unclaimed: unclaimedValue,
        lastUpdateTime: lastUpdateTimeValue
      })

      // 设置格式化后的金额
      setFormattedUnclaimed(formatAmount(unclaimedValue))
      setFormattedTotalClaimed(formatAmount(totalClaimedValue))
      setFormattedLiquidity(formatAmount(liquidityValue))
    } catch (error) {
      console.error('获取流动性奖励信息失败:', error)
    } finally {
      if (isInitialLoad) setIsLoading(false)
    }
  }, [library, userAddress, formatAmount])

  useEffect(() => {
    fetchLiquidityRewardInfo(true)
  }, [fetchLiquidityRewardInfo])

  return {
    liquidityInfo,
    isLoading,
    formattedUnclaimed,
    formattedTotalClaimed,
    formattedLiquidity,
    refresh: () => fetchLiquidityRewardInfo(false)
  }
}

/**
 * 获取交易暂停状态
 * @returns 交易是否被暂停
 */
export function usePauseSwapStatus(): {
  isPaused: boolean | undefined
  isLoading: boolean
  refresh: () => Promise<void>
} {
  const { library } = useActiveWeb3React()
  const [isPaused, setIsPaused] = useState<boolean | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)

  const fetchPauseSwapStatus = useCallback(async (isInitialLoad = false) => {
    if (!library) return

    if (isInitialLoad) setIsLoading(true)
    try {
      // 获取Router合约ABI
      const routerAbi = abi

      const routerContract = new ethers.Contract(ROUTER_ADDRESS, routerAbi, library)
      const pauseStatus = await routerContract.pauseSwap()

      setIsPaused(pauseStatus)
    } catch (error) {
      console.error('获取交易暂停状态失败:', error)
    } finally {
      if (isInitialLoad) setIsLoading(false)
    }
  }, [library])

  useEffect(() => {
    fetchPauseSwapStatus(true)
  }, [fetchPauseSwapStatus])

  return {
    isPaused,
    isLoading,
    refresh: () => fetchPauseSwapStatus(false)
  }
}

/**
 * 获取总未领取奖励
 * @returns 总未领取奖励信息
 */
export function useTotalUnclaimed(): {
  totalUnclaimed: JSBI | undefined
  isLoading: boolean
  formattedAmount: string
  refresh: () => Promise<void>
} {
  const { library } = useActiveWeb3React()
  const [totalUnclaimed, setTotalUnclaimed] = useState<JSBI | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [formattedAmount, setFormattedAmount] = useState('0')

  const fetchTotalUnclaimed = useCallback(async (isInitialLoad = false) => {
    if (!library) return

    if (isInitialLoad) setIsLoading(true)
    try {
      const routerContract = new ethers.Contract(ROUTER_ADDRESS, abi, library)
      const unclaimed = await routerContract.totalUnclaimed()

      // 转换为JSBI
      const amount = JSBI.BigInt(unclaimed.toString())
      setTotalUnclaimed(amount)

      // 格式化显示金额
      const etherValue = parseFloat(ethers.utils.formatEther(unclaimed))
      if (etherValue === 0) {
        setFormattedAmount('0 MUD')
      } else if (etherValue < 0.0001) {
        setFormattedAmount('< 0.0001 MUD')
      } else {
        const truncated = Math.floor(etherValue * 10000) / 10000
        setFormattedAmount(`${truncated.toFixed(4)} MUD`)
      }
    } catch (error) {
      console.error('获取总未领取奖励失败:', error)
    } finally {
      if (isInitialLoad) setIsLoading(false)
    }
  }, [library])

  useEffect(() => {
    fetchTotalUnclaimed(true)
  }, [fetchTotalUnclaimed])

  return {
    totalUnclaimed,
    isLoading,
    formattedAmount,
    refresh: () => fetchTotalUnclaimed(false)
  }
}

/**
 * 获取已存入的MUD余额
 * @returns MUD余额信息
 */
export function useDepositedMUDBalance(): {
  balance: JSBI | undefined
  isLoading: boolean
  formattedAmount: string
  refresh: () => Promise<void>
} {
  const { library } = useActiveWeb3React()
  const [balance, setBalance] = useState<JSBI | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [formattedAmount, setFormattedAmount] = useState('0')

  const MUD_CONTRACT_ADDRESS = '0x82f9d23cB62Ec0016109B7C4b8dB34890FdBA0F0'
  const DEPOSIT_ADDRESS = '0x87a9Ca922c356EeE83FA8B7D04B63715feEFEB1C'

  const fetchBalance = useCallback(async (isInitialLoad = false) => {
    if (!library) return

    if (isInitialLoad) setIsLoading(true)
    try {
      const mudContract = new ethers.Contract(MUD_CONTRACT_ADDRESS, ERC20_INTERFACE, library)
      const balanceResult = await mudContract.balanceOf(DEPOSIT_ADDRESS)

      // 转换为JSBI
      const amount = JSBI.BigInt(balanceResult.toString())
      setBalance(amount)

      // 格式化显示金额（除以10^18）
      const etherValue = parseFloat(ethers.utils.formatEther(balanceResult))
      if (etherValue === 0) {
        setFormattedAmount('0')
      } else if (etherValue < 0.0001) {
        setFormattedAmount('< 0.0001')
      } else {
        const truncated = Math.floor(etherValue * 100) / 100
        setFormattedAmount(`${truncated.toFixed(2)}`)
      }
    } catch (error) {
      console.error('获取已存入MUD余额失败:', error)
    } finally {
      if (isInitialLoad) setIsLoading(false)
    }
  }, [library])

  useEffect(() => {
    fetchBalance(true)
  }, [fetchBalance])

  return {
    balance,
    isLoading,
    formattedAmount,
    refresh: () => fetchBalance(false)
  }
}

/**
 * 获取已存入的USDT余额
 * @returns USDT余额信息
 */
export function useDepositedUSDTBalance(): {
  balance: JSBI | undefined
  isLoading: boolean
  formattedAmount: string
  refresh: () => Promise<void>
} {
  const { library } = useActiveWeb3React()
  const [balance, setBalance] = useState<JSBI | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [formattedAmount, setFormattedAmount] = useState('0')

  const USDT_CONTRACT_ADDRESS = '0xB82Db19f90daA3F40F25f7aC71786E7268139b0E'
  const DEPOSIT_ADDRESS = '0x87a9Ca922c356EeE83FA8B7D04B63715feEFEB1C'

  const fetchBalance = useCallback(async (isInitialLoad = false) => {
    if (!library) return

    if (isInitialLoad) setIsLoading(true)
    try {
      const usdtContract = new ethers.Contract(USDT_CONTRACT_ADDRESS, ERC20_INTERFACE, library)
      const balanceResult = await usdtContract.balanceOf(DEPOSIT_ADDRESS)

      // 转换为JSBI
      const amount = JSBI.BigInt(balanceResult.toString())
      setBalance(amount)

      // 格式化显示金额（除以10^6）
      const value = parseFloat(balanceResult.toString()) / 1000000
      if (value === 0) {
        setFormattedAmount('0')
      } else if (value < 0.0001) {
        setFormattedAmount('< 0.0001')
      } else {
        const truncated = Math.floor(value * 100) / 100
        setFormattedAmount(`${truncated.toFixed(2)}`)
      }
    } catch (error) {
      console.error('获取已存入USDT余额失败:', error)
    } finally {
      if (isInitialLoad) setIsLoading(false)
    }
  }, [library])

  useEffect(() => {
    fetchBalance(true)
  }, [fetchBalance])

  return {
    balance,
    isLoading,
    formattedAmount,
    refresh: () => fetchBalance(false)
  }
}

/**
 * 获取路由合约的 owner 地址
 * @returns owner 地址信息
 */
export function useRouterOwner(): {
  owner: string | undefined
  isLoading: boolean
  refresh: () => Promise<void>
} {
  const { library } = useActiveWeb3React()
  const [owner, setOwner] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)

  const fetchOwner = useCallback(async (isInitialLoad = false) => {
    if (!library) return

    if (isInitialLoad) setIsLoading(true)
    try {
      const routerContract = new ethers.Contract(ROUTER_ADDRESS, abi, library)
      const ownerAddress = await routerContract.owner()
      setOwner(ownerAddress)
    } catch (error) {
      console.error('获取路由合约 owner 失败:', error)
    } finally {
      if (isInitialLoad) setIsLoading(false)
    }
  }, [library])

  useEffect(() => {
    fetchOwner(true)
  }, [fetchOwner])

  return {
    owner,
    isLoading,
    refresh: () => fetchOwner(false)
  }
}

/**
 * 获取年度流动性奖励百分比
 * @returns 年度流动性奖励百分比信息
 */
export function usePerLiquidityRewardYearPercent(): {
  percent: number | undefined
  isLoading: boolean
  refresh: () => Promise<void>
} {
  const { library } = useActiveWeb3React()
  const [percent, setPercent] = useState<number | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)

  const fetchPerLiquidityRewardYear = useCallback(async (isInitialLoad = false) => {
    if (!library) return

    if (isInitialLoad) setIsLoading(true)
    try {
      const routerContract = new ethers.Contract(ROUTER_ADDRESS, abi, library)
      const perLiquidityRewardYear = await routerContract.perLiquidityRewardYear()

      // 将返回值乘以100后除以固定值，得到百分比
      // 固定值 15791661046371634804015.35 * 100 = 1579166104637163480401535
      const DENOMINATOR = BigInt('1579166104637163480401535')

      // 将返回值转换为JSBI类型
      const rewardValueBigInt = perLiquidityRewardYear

      // 乘以100
      const rewardValueTimes100 = BigInt(rewardValueBigInt) * BigInt(100)

      // 除以分母得到百分比
      const percentJSBI = parseInt(rewardValueTimes100.toString()) / parseInt(DENOMINATOR.toString())

      // 转换为数字
      const percentNumber = parseInt(percentJSBI.toString())

      setPercent(percentNumber)
    } catch (error) {
      console.error('获取年度流动性奖励百分比失败:', error)
    } finally {
      if (isInitialLoad) setIsLoading(false)
    }
  }, [library])

  useEffect(() => {
    fetchPerLiquidityRewardYear(true)
  }, [fetchPerLiquidityRewardYear])

  return {
    percent,
    isLoading,
    refresh: () => fetchPerLiquidityRewardYear(false)
  }
}

/**
 * 结算所有流动性提供者的奖励
 * @returns 结算状态及方法
 */
export function useSettleAllRewards(): {
  settleAllRewards: () => Promise<void>
  isSettling: boolean
} {
  const { account, library } = useActiveWeb3React()
  const [isSettling, setIsSettling] = useState(false)
  const addTransaction = useTransactionAdder()

  const settleAllRewards = useCallback(async () => {
    if (!library || !account) {
      toast.error('钱包未连接，请先连接钱包', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      })
      return
    }

    try {
      setIsSettling(true)

      // 创建带有签名者的合约实例
      const signer = library.getSigner(account)
      const routerContract = new ethers.Contract(ROUTER_ADDRESS, abi, signer)

      // 获取流动性提供者数量
      const lpLength = await routerContract.getLiquidityProvidersLength()
      const lpCount = parseInt(lpLength.toString())

      if (lpCount === 0) {
        console.error('没有流动性提供者')
        toast.warning('没有流动性提供者需要结算', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        })
        setIsSettling(false)
        return
      }

      // 调用结算方法
      const response = await routerContract.settleReward(0, lpCount)

      addTransaction(response, {
        summary: `结算 ${lpCount} 个流动性提供者的奖励`
      })

      // 等待交易完成
      await response.wait()

      // 更新成功提示
      toast.success(`成功结算 ${lpCount} 个流动性提供者的奖励`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      })

      // 刷新页面
      window.location.reload()

      console.log('结算完成')
    } catch (error) {
      console.error('结算奖励失败:', error)

      // 显示错误提示
      toast.error(`结算失败: ${(error as any).data.message}`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      })
    } finally {
      setIsSettling(false)
    }
  }, [library, account, addTransaction])

  return {
    settleAllRewards,
    isSettling
  }
}

/**
 * 更新年度流动性奖励百分比
 * @returns 更新年度流动性奖励百分比的方法和状态
 */
export function useUpdateLiquidityRewardPercent(): {
  updatePercent: (percent: number) => Promise<void>
  isUpdating: boolean
} {
  const { account, library } = useActiveWeb3React()
  const [isUpdating, setIsUpdating] = useState(false)
  const addTransaction = useTransactionAdder()

  const updatePercent = useCallback(
    async (percent: number) => {
      if (!library || !account) {
        toast.error('钱包未连接，请先连接钱包', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        })
        return
      }

      try {
        setIsUpdating(true)

        // 创建带有签名者的合约实例
        const signer = library.getSigner(account)
        const routerContract = new ethers.Contract(ROUTER_ADDRESS, abi, signer)

        // 计算新的年化收益率
        // 固定值 * 百分比 / 100
        const DENOMINATOR = JSBI.BigInt('1579166104637163480401535')
        const percentBigInt = JSBI.BigInt(percent)
        const newRewardValue = JSBI.divide(JSBI.multiply(DENOMINATOR, percentBigInt), JSBI.BigInt(100))

        // 调用合约更新方法
        const response = await routerContract.setPerLiquidityRewardYear(newRewardValue.toString())

        addTransaction(response, {
          summary: `更新年化收益率为 ${percent}%`
        })

        // 等待交易完成
        await response.wait()

        // 更新成功提示
        toast.success(`成功更新年化收益率为 ${percent}%`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        })

        console.log('年化收益率更新完成')
      } catch (error) {
        console.error('更新年化收益率失败:', error)

        // 显示错误提示
        toast.error(`更新失败: ${(error as any).data.message}`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        })
      } finally {
        setIsUpdating(false)
      }
    },
    [library, account, addTransaction]
  )

  return {
    updatePercent,
    isUpdating
  }
}

/**
 * 单独获取单个代币余额的 hook，不使用 multicall
 * 用于解决代币列表过多时 multicall 超时的问题
 */
export function useSingleTokenBalance(
  account?: string,
  token?: Token
): {
  balance: TokenAmount | undefined
  loading: boolean
  error: boolean
  retry: () => void
} {
  const { library } = useActiveWeb3React()
  const [balance, setBalance] = useState<TokenAmount | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const fetchBalance = useCallback(async () => {
    if (!account || !token || !library) {
      setBalance(undefined)
      setLoading(false)
      setError(false)
      return
    }

    setLoading(true)
    setError(false)

    try {
      const contract = new ethers.Contract(token.address, ERC20_INTERFACE, library)
      const result = await contract.balanceOf(account)
      const amount = JSBI.BigInt(result.toString())
      setBalance(new TokenAmount(token, amount))
    } catch (err) {
      console.error('Failed to fetch token balance:', err)
      setError(true)
      setBalance(undefined)
    } finally {
      setLoading(false)
    }
  }, [account, token, library])

  const retry = useCallback(() => {
    fetchBalance()
  }, [fetchBalance])

  useEffect(() => {
    fetchBalance()
  }, [fetchBalance])

  return { balance, loading, error, retry }
}

/**
 * 带缓存的单个代币余额 hook
 */
const balanceCache = new Map<string, { balance: TokenAmount | undefined; timestamp: number }>()
const CACHE_DURATION = 30000 // 30秒缓存

export function useCachedSingleTokenBalance(
  account?: string,
  token?: Token
): {
  balance: TokenAmount | undefined
  loading: boolean
  error: boolean
  retry: () => void
} {
  const { library } = useActiveWeb3React()
  const [balance, setBalance] = useState<TokenAmount | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const cacheKey = useMemo(() => {
    if (!account || !token) return null
    return `${account}-${token.address}`
  }, [account, token])

  const fetchBalance = useCallback(
    async (forceRefresh = false) => {
      if (!account || !token || !library || !cacheKey) {
        setBalance(undefined)
        setLoading(false)
        setError(false)
        return
      }

      // 检查缓存
      if (!forceRefresh) {
        const cached = balanceCache.get(cacheKey)
        if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
          setBalance(cached.balance)
          setLoading(false)
          setError(false)
          return
        }
      }

      setLoading(true)
      setError(false)

      try {
        const contract = new ethers.Contract(token.address, ERC20_INTERFACE, library)
        const result = await contract.balanceOf(account)
        const amount = JSBI.BigInt(result.toString())
        const tokenAmount = new TokenAmount(token, amount)

        // 更新缓存
        balanceCache.set(cacheKey, { balance: tokenAmount, timestamp: Date.now() })
        setBalance(tokenAmount)
      } catch (err) {
        console.error('Failed to fetch token balance:', err)
        setError(true)
        setBalance(undefined)
      } finally {
        setLoading(false)
      }
    },
    [account, token, library, cacheKey]
  )

  const retry = useCallback(() => {
    fetchBalance(true)
  }, [fetchBalance])

  useEffect(() => {
    fetchBalance()
  }, [fetchBalance])

  return { balance, loading, error, retry }
}

/**
 * 带自动重试的单个代币余额 hook
 */
export function useResilientSingleTokenBalance(
  account?: string,
  token?: Token,
  maxRetries = 3
): {
  balance: TokenAmount | undefined
  loading: boolean
  error: boolean
  retry: () => void
} {
  const { library } = useActiveWeb3React()
  const [balance, setBalance] = useState<TokenAmount | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const retryCountRef = useRef(0)

  const cacheKey = useMemo(() => {
    if (!account || !token) return null
    return `${account}-${token.address}`
  }, [account, token])

  const fetchBalance = useCallback(
    async (forceRefresh = false) => {
      if (!account || !token || !library || !cacheKey) {
        setBalance(undefined)
        setLoading(false)
        setError(false)
        return
      }

      // 检查缓存
      if (!forceRefresh) {
        const cached = balanceCache.get(cacheKey)
        if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
          setBalance(cached.balance)
          setLoading(false)
          setError(false)
          return
        }
      }

      setLoading(true)
      setError(false)

      const attemptFetch = async (attemptCount: number): Promise<void> => {
        try {
          const contract = new ethers.Contract(token.address, ERC20_INTERFACE, library)
          const result = await contract.balanceOf(account)
          const amount = JSBI.BigInt(result.toString())
          const tokenAmount = new TokenAmount(token, amount)

          // 更新缓存
          balanceCache.set(cacheKey, { balance: tokenAmount, timestamp: Date.now() })
          setBalance(tokenAmount)
          retryCountRef.current = 0 // 成功后重置重试计数
        } catch (err) {
          console.error(`Failed to fetch token balance (attempt ${attemptCount + 1}):`, err)

          if (attemptCount < maxRetries - 1) {
            // 延迟重试，递增延迟时间
            const delay = Math.min(1000 * Math.pow(2, attemptCount), 5000)
            setTimeout(() => attemptFetch(attemptCount + 1), delay)
          } else {
            setError(true)
            setBalance(undefined)
            retryCountRef.current = 0
          }
        }
      }

      try {
        await attemptFetch(0)
      } finally {
        setLoading(false)
      }
    },
    [account, token, library, cacheKey, maxRetries]
  )

  const retry = useCallback(() => {
    retryCountRef.current = 0
    fetchBalance(true)
  }, [fetchBalance])

  useEffect(() => {
    fetchBalance()
  }, [fetchBalance])

  return { balance, loading, error, retry }
}
